import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, AlertCircle, Phone, MapPin, Globe, AlertTriangle, CalendarClock, ClipboardList, UserCheck, CheckCircle2, XCircle, Clock, Pencil } from 'lucide-react';
import { useClientProfile } from '@/features/clients/useClientProfile';
import { clientsApi } from '@/features/clients/clients.api';
import { AssignmentAction } from '@/features/clients/AssignmentAction';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { PromotedBanner } from '@/features/clients/PromotedBanner';
import { ReferralChainCard } from '@/features/clients/ReferralChainCard';
import { LinkedHouseholdPromotionsCard } from '@/features/clients/LinkedHouseholdPromotionsCard';
import { ClientProfileModal } from '@/features/clients/ClientProfileModal';
import { EditClientInfoModal } from '@/features/clients/EditClientInfoModal';
import { ScreeningReviewModal } from '@/features/screening/ScreeningReviewModal';
import { toast } from '@/stores/toast.store';

/**
 * T2-G (#151a) — los roles que `GET /screenings/:id/review` acepta (el
 * `@Roles` de la clase en `screenings.controller.ts`). Fuera de estos, el
 * botón no se dibuja: sería un botón que siempre falla.
 */
const ROLES_QUE_REVISAN = new Set(['agent', 'supervisor', 'admin']);

/**
 * T2-G (#151a, decisión de Edd 23-oct: «solo en los screenings propios») —
 * «Ver respuestas» aparece SOLO si el screening es del usuario que mira, está
 * ENVIADO y no está borrado. La propiedad la vuelve a decidir el servidor
 * (`review()` → 403 `SCREENING_NOT_YOURS`); esto solo evita ofrecer un botón
 * que el servidor rechazaría.
 */
function puedeVerRespuestas(
  s: { agentId: string | null; isSubmitted: boolean; deleted?: boolean },
  userId: string | null | undefined,
  userRole: string | null | undefined,
): boolean {
  if (!userId || !userRole || !ROLES_QUE_REVISAN.has(userRole)) return false;
  if (s.deleted === true || !s.isSubmitted || s.agentId == null) return false;
  return String(s.agentId) === String(userId);
}

/**
 * Read-only patient profile page. Reached from:
 *  - Global PatientSearchBar (any role except uploader).
 *  - 'View profile' buttons in BookingsListModal, MissedBookingsPage,
 *    QueuePage row context (Bug-User-08, 10).
 *
 * Backend audit-logs each open as VIEW_CLIENT (HIPAA). Uploader role is
 * blocked at the backend (@Roles decorator returns 403); the link is also
 * hidden in their UI.
 */
export function ClientProfilePage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clientId } = useParams<{ clientId: string }>();
  const userRole = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);

  const { data, error, isLoading } = useClientProfile(clientId);

  // Admin-only inline form: reset client's call-attempt counter.
  // Visible when role=admin AND attemptCount > 0. Backend enforces min 3
  // chars for reason. Audit-logged with previousCount + notif to last agent
  // (Cluster A followup — A-3).
  const [resetOpen, setResetOpen] = useState(false);
  const [resetReason, setResetReason] = useState('');

  // Cluster H v2 Fase 2.7 — overlay modal state. When set, the
  // ClientProfileModal mounts with that clientId. Cards inside the modal
  // call onNavigate(id) which replaces this value (single-modal nested nav).
  const [modalClientId, setModalClientId] = useState<string | null>(null);
  // v0.6.1 Fase 9 — Cluster ED Phase 1.
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  // T2-G (#151a) — el screening cuya revisión está abierta («Ver respuestas»).
  const [reviewId, setReviewId] = useState<string | null>(null);
  const resetMut = useMutation({
    mutationFn: () => clientsApi.resetAttempts(data!.id, resetReason.trim()),
    onSuccess: () => {
      toast.success(
        t('clientProfile.resetAttempts.successTitle'),
        t('clientProfile.resetAttempts.successBody'),
      );
      setResetOpen(false);
      setResetReason('');
      queryClient.invalidateQueries({ queryKey: ['clients.profile', data!.id] });
    },
    onError: (err) => {
      toast.error(t('clientProfile.resetAttempts.errorTitle'), (err as Error).message);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 text-nav-teal animate-spin" aria-label={t('clientProfile.loading')} />
      </div>
    );
  }

  if (error || !data) {
    const status = (error as { status?: unknown } | null)?.status;
    const isNotFound = status === 404;
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-nav-red mb-4" aria-hidden />
        <h1 className="font-head text-lg font-bold text-nav-navy mb-2">
          {isNotFound ? t('clientProfile.notFound.title') : t('clientProfile.error.title')}
        </h1>
        <p className="text-sm text-nav-slate mb-6">
          {isNotFound ? t('clientProfile.notFound.body') : t('clientProfile.error.body')}
        </p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('clientProfile.back')}
        </Button>
      </div>
    );
  }

  const phones = [data.phoneCell, data.phoneHome, data.phoneOther].filter(Boolean) as string[];
  const isAgentOrAdmin = userRole === 'agent' || userRole === 'admin' || userRole === 'supervisor';
  const canAct = !!userRole && !!userId;
  // F5-T2-4: cuando el agente ES el dueño, la accion de llamada vive en el
  // pie (verificacion previa) — la tarjeta no la duplica. Para dueño ajeno o
  // sin asignacion, la tarjeta conserva sus acciones de reasignacion.
  const esDuenioAgente =
    userRole === 'agent' &&
    !!data?.activeAssignment &&
    String(data.activeAssignment.agentId) === String(userId);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('clientProfile.back')}
        </Button>
        <div className="flex items-center gap-2">
          {/* F5-T2-4 (mockup Agente 04, ventana 5): el chip «En tu cola». */}
          {data.activeAssignment && String(data.activeAssignment.agentId) === String(userId) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-nav-sageTint text-nav-sageText border border-nav-sage text-xs font-medium">
              <UserCheck className="h-3.5 w-3.5" aria-hidden />
              {t('clientProfile.inYourQueue')}
            </span>
          )}
          {data.doNotCall && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-nav-redTint text-nav-red border border-nav-red/40 text-xs font-medium">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {t('clientProfile.dnc.badge')}
            </span>
          )}
        </div>
      </div>

      <header className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-head text-xl font-bold text-nav-navy">
              {data.lastName}, {data.firstName}
            </h1>

      {data.promotedFrom && (
        <PromotedBanner
          parentClientName={data.promotedFrom.parentClientName}
          parentScreeningId={data.promotedFrom.parentScreeningId}
          promotedAt={data.promotedFrom.promotedAt}
          promotedByName={data.promotedFrom.promotedByName}
          promotedByEmail={data.promotedFrom.promotedByEmail}
        />
      )}
            <p className="text-sm text-nav-slate mt-1">
              MRN ···{data.cinLast6} · DOB {data.dob}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="px-2 py-1 rounded-md bg-nav-hair text-nav-navy text-xs">
              {t('clientProfile.status')}: <span className="font-medium">{data.screeningStatus}</span>
            </span>
            {data.isBronxZip && (
              <span className="px-2 py-1 rounded-md bg-nav-sageTint text-nav-sageText text-xs">{t('clientProfile.bronx')}</span>
            )}
            {/* v0.6.1 Fase 9 — Cluster ED Phase 1 trigger. Admin/supervisor
                always see it; agent only sees + can submit if they own an
                active assignment (server enforces). The visible button is
                permissive — backend 403 surfaces as a toast error if not
                allowed, which is acceptable v0.6.1 Phase 1 UX. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditInfoOpen(true)}
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {t('editClientInfo.triggerLabel')}
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
          <div className="flex items-start gap-2 text-nav-navy">
            <Phone className="h-4 w-4 mt-0.5 text-nav-slate" aria-hidden />
            <div>
              {phones.length === 0 && <span className="text-nav-slate">{t('clientProfile.noPhone')}</span>}
              {phones.map((p, i) => (
                <div key={i}>{p}</div>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-2 text-nav-navy">
            <MapPin className="h-4 w-4 mt-0.5 text-nav-slate" aria-hidden />
            <div>
              {data.addressLine1 ?? <span className="text-nav-slate">{t('clientProfile.noAddress')}</span>}
              <div className="text-nav-slate text-xs">
                {[data.city, data.postalCode].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-nav-navy">
            <Globe className="h-4 w-4 text-nav-slate" aria-hidden />
            <span>{data.preferredLanguage ?? 'en'}</span>
          </div>
          <div className="flex items-center gap-2 text-nav-navy flex-wrap">
            <ClipboardList className="h-4 w-4 text-nav-slate" aria-hidden />
            <span>{t('clientProfile.attemptCount', { n: String(data.attemptCount) })}</span>
            {userRole === 'admin' && data.attemptCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setResetOpen((o) => !o)}
              >
                {t('clientProfile.resetAttempts.action')}
              </Button>
            )}
          </div>
        </div>

        {userRole === 'admin' && resetOpen && (
          <div className="mt-3 p-3 rounded-md bg-nav-softer border border-nav-card">
            <label htmlFor="reset-attempts-reason" className="text-xs font-medium text-nav-navy">
              {t('clientProfile.resetAttempts.reasonLabel')}
            </label>
            <textarea
              id="reset-attempts-reason"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              className="w-full text-sm border border-nav-card rounded p-2 mt-1"
              rows={2}
              placeholder={t('clientProfile.resetAttempts.reasonPlaceholder')}
              maxLength={500}
            />
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                onClick={() => resetMut.mutate()}
                disabled={resetReason.trim().length < 3 || resetMut.isPending}
              >
                {t('clientProfile.resetAttempts.confirm')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setResetOpen(false);
                  setResetReason('');
                }}
              >
                {t('clientProfile.resetAttempts.cancel')}
              </Button>
            </div>
          </div>
        )}

        {data.doNotCall && data.dncReason && (
          <div className="mt-4 px-3 py-2 rounded-md bg-nav-redTint text-nav-red text-xs">
            <strong>{t('clientProfile.dnc.reason')}:</strong> {data.dncReason}
          </div>
        )}
      </header>

      {/* Cluster H v2 Fase 2.7 — bidirectional referral chain.
          Inbound (peach) and outbound (mint) cards open the related client
          in the ClientProfileModal overlay (no page navigation, no
          target=_blank). Both render conditionally; the page stays clean
          when neither side of the chain exists. */}
      {data.referredBy ? (
        <ReferralChainCard
          parentClientId={data.referredBy.parentClientId}
          parentClientLastName={data.referredBy.parentClientLastName}
          parentClientCinLast6={data.referredBy.parentClientCinLast6}
          promotedAt={data.referredBy.promotedAt}
          promotedByAgentName={data.referredBy.promotedByAgentName}
          onParentClick={setModalClientId}
        />
      ) : null}

      <LinkedHouseholdPromotionsCard
        promotions={data.linkedHouseholdPromotions}
        onItemClick={setModalClientId}
      />

      <section className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-5">
        <h2 className="font-head text-sm font-bold text-nav-navy mb-3 flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-nav-slate" aria-hidden /> {t('clientProfile.activeAssignment')}
        </h2>
        {data.activeAssignment ? (
          <div className="text-sm text-nav-navy space-y-1">
            <div>
              <span className="text-nav-slate">{t('clientProfile.agent')}:</span>{' '}
              {data.activeAssignment.agentName ?? data.activeAssignment.agentId}
            </div>
            <div>
              <span className="text-nav-slate">{t('clientProfile.status')}:</span> {data.activeAssignment.status}
            </div>
            <div>
              <span className="text-nav-slate">{t('clientProfile.assignedAt')}:</span>{' '}
              {new Date(data.activeAssignment.assignedAt).toLocaleString()}
            </div>
            {canAct && !esDuenioAgente && (
              <div className="pt-3">
                <AssignmentAction
                  profile={data}
                  currentUser={{ id: userId as string, role: userRole as string }}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-nav-slate">{t('clientProfile.noActiveAssignment')}</p>
            {canAct && (
              <AssignmentAction
                profile={data}
                currentUser={{ id: userId as string, role: userRole as string }}
              />
            )}
          </div>
        )}
      </section>

      <section className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-5">
        <h2 className="font-head text-sm font-bold text-nav-navy mb-3 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-nav-slate" aria-hidden /> {t('clientProfile.bookings')}
        </h2>
        {data.bookings.length === 0 ? (
          <p className="text-sm text-nav-slate">{t('clientProfile.noBookings')}</p>
        ) : (
          <ul className="divide-y divide-nav-hair">
            {data.bookings.map((b) => {
              // A-14: per-status icon for quick visual scan
              const StatusIcon =
                b.status === 'completed' ? CheckCircle2
                : b.status === 'missed' ? AlertTriangle
                : b.status === 'cancelled' ? XCircle
                : Clock; // scheduled / rescheduled / fallback
              const statusColor =
                b.status === 'completed' ? 'text-nav-sageMid'
                : b.status === 'missed' ? 'text-nav-red'
                : b.status === 'cancelled' ? 'text-nav-faint'
                : 'text-nav-teal';
              return (
                <li key={b.id} className="py-2 text-sm flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <StatusIcon className={`h-4 w-4 mt-0.5 ${statusColor} shrink-0`} aria-hidden />
                    <div>
                      <div className="text-nav-navy">{new Date(b.scheduledAt).toLocaleString()}</div>
                      <div className="text-xs text-nav-slate">
                        {t('clientProfile.agent')}: {b.agentName ?? b.agentId} · {b.status}
                      </div>
                      {b.notes && <div className="text-xs text-nav-slate mt-1">{b.notes}</div>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="bg-white border-[1.5px] border-nav-card rounded-[12px] p-5">
        <h2 className="font-head text-sm font-bold text-nav-navy mb-3 flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-nav-slate" aria-hidden /> {t('clientProfile.recentScreenings')}
        </h2>
        {data.recentScreenings.length === 0 ? (
          <p className="text-sm text-nav-slate">{t('clientProfile.noScreenings')}</p>
        ) : (
          <ul className="divide-y divide-nav-hair">
            {data.recentScreenings.map((s) => {
              // T2-G (#151a) — un screening borrado (cancelado o barrido por
              // el reaper) dice «Cancelado», no «En progreso», y nunca ofrece
              // «Ver respuestas». La API vieja no manda `deleted`: entonces la
              // fila queda como antes.
              const cancelado = s.deleted === true;
              const estado = cancelado
                ? 'cancelado'
                : s.isSubmitted
                  ? 'enviado'
                  : s.completedAt
                    ? 'completado'
                    : 'en-curso';
              const verRespuestas = puedeVerRespuestas(s, userId, userRole);
              return (
                <li
                  key={s.id}
                  className="py-2 text-sm flex items-center justify-between gap-3"
                  data-t2g-screening={s.id}
                  data-t2g-estado={estado}
                >
                  <div className="min-w-0">
                    <div className="text-nav-navy">{new Date(s.startedAt).toLocaleString()}</div>
                    <div className="text-xs text-nav-slate">
                      {t('clientProfile.agent')}: {s.agentName ?? s.agentId ?? '—'}
                      {' · '}
                      {cancelado
                        ? t('clientProfile.cancelled')
                        : s.isSubmitted
                          ? t('clientProfile.submitted')
                          : s.completedAt
                            ? t('clientProfile.completed')
                            : t('clientProfile.inProgress')}
                    </div>
                  </div>
                  {verRespuestas && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setReviewId(s.id)}
                      data-t2g="ver-respuestas"
                    >
                      {t('clientProfile.viewAnswers')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* F5-T2-4 (mockup Agente 04, ventana 5) — la VERIFICACIÓN PREVIA al pie:
          enumera por qué se puede llamar, y si no se puede, cambia a ámbar con
          la razón y el botón DESAPARECE — no un botón que falla al apretarlo.
          Solo checks MEDIBLES en el modelo real (sin no-llamar · teléfono
          registrado · asignado a tu cola). «Cobertura» y «franja horaria» del
          dibujo NO tienen dato en este modelo — desviación anotada en el acta,
          no se finge un check que nada mide. Solo para agentes. El CTA verde
          es AssignmentAction — el pie es el UNICO dueño de la accion de
          llamada en el caso propio (la tarjeta lo cede) y hereda el preflight
          A-2: si el reaper revoco el claim entre la carga y el click, toast
          claro en vez de un Bad Request en /pre-call. */}
      {userRole === 'agent' && (() => {
        const mine = !!data.activeAssignment && String(data.activeAssignment.agentId) === String(userId);
        const razones: string[] = [];
        if (data.doNotCall) razones.push(t('clientProfile.precheck.blockedDnc'));
        if (phones.length === 0) razones.push(t('clientProfile.precheck.blockedNoPhone'));
        if (!data.activeAssignment) razones.push(t('clientProfile.precheck.blockedNoAssignment'));
        else if (!mine) razones.push(t('clientProfile.precheck.blockedOtherOwner', {
          name: data.activeAssignment.agentName ?? data.activeAssignment.agentId,
        }));
        const ok = razones.length === 0;
        return ok ? (
          <section
            className="bg-nav-sageTint border-[1.5px] border-nav-sage rounded-[12px] p-4 flex items-center justify-between gap-4 flex-wrap"
            data-t2="precheck-ok"
          >
            <div className="flex items-start gap-2 text-sm text-nav-sageText">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
              <span>
                {t('clientProfile.precheck.okLine', { n: String(data.attemptCount) })}
              </span>
            </div>
            <AssignmentAction
              profile={data}
              currentUser={{ id: userId as string, role: userRole as string }}
            />
          </section>
        ) : (
          <section
            className="bg-nav-amberTint border-[1.5px] border-[#e6cf9a] rounded-[12px] p-4 text-sm text-nav-amberText"
            data-t2="precheck-blocked"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
              <div>
                <p className="font-medium">{t('clientProfile.precheck.blockedTitle')}</p>
                <ul className="list-disc ml-4 mt-1">
                  {razones.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
          </section>
        );
      })()}

      {!isAgentOrAdmin && (
        <p className="text-xs text-nav-slate text-center">{t('clientProfile.readOnlyNotice')}</p>
      )}

      {/* Cluster H v2 Fase 2.7 — overlay modal. AnimatePresence inside the
          modal handles mount/unmount animation; the component renders
          nothing visible when modalClientId is null. */}
      <ClientProfileModal
        clientId={modalClientId}
        onClose={() => setModalClientId(null)}
        onNavigate={setModalClientId}
      />

      {/* T2-G (#151a) — la MISMA revisión que abre «Mis números» → «Screenings
          enviados», ahora también desde el perfil. Montada solo con un id. */}
      {reviewId && (
        <ScreeningReviewModal screeningId={reviewId} onClose={() => setReviewId(null)} />
      )}

      {/* v0.6.1 Fase 9 — Cluster ED Phase 1: edit address + phones modal. */}
      <EditClientInfoModal
        open={editInfoOpen}
        onClose={() => setEditInfoOpen(false)}
        client={{
          id: data.id,
          firstName: data.firstName,
          lastName: data.lastName,
          addressLine1: data.addressLine1,
          city: data.city,
          postalCode: data.postalCode,
          isBronxZip: data.isBronxZip,
          phoneHome: data.phoneHome,
          phoneCell: data.phoneCell,
        }}
      />
    </div>
  );
}
